import React from 'react';
import { Star, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials } from '../mock';

const Testimonials = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1733431772808-82d878e59000?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxkcnl3YWxsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3NDQ4Mjc2Mnww&ixlib=rb-4.1.0&q=85"
            alt="Testimonials Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Client Testimonials</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Don't just take our word for it - hear what our clients have to say
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#00AEEF] text-[#00AEEF]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic text-lg">"{testimonial.text}"</p>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="font-bold text-gray-900 text-lg">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                    <div className="text-sm text-[#00AEEF] mt-1 font-medium">{testimonial.project}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Our Track Record</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Numbers that speak for themselves
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#00AEEF] mb-2">500+</div>
                <div className="text-gray-600 font-medium">Projects Completed</div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#00AEEF] mb-2">100%</div>
                <div className="text-gray-600 font-medium">Client Satisfaction</div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#00AEEF] mb-2">15+</div>
                <div className="text-gray-600 font-medium">Years Experience</div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#00AEEF] mb-2">24/7</div>
                <div className="text-gray-600 font-medium">Customer Support</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Join Hundreds of Satisfied Customers
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              From homeowners to large construction firms, our clients trust us to deliver exceptional quality and service on every project. Let us show you why we're the preferred choice for drywall and insulation services.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <div className="bg-gray-50 px-6 py-4 rounded-lg">
                <div className="text-2xl font-bold text-[#00AEEF]">✓</div>
                <div className="text-sm text-gray-600 mt-1">Licensed & Insured</div>
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-lg">
                <div className="text-2xl font-bold text-[#00AEEF]">✓</div>
                <div className="text-sm text-gray-600 mt-1">Quality Guarantee</div>
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-lg">
                <div className="text-2xl font-bold text-[#00AEEF]">✓</div>
                <div className="text-sm text-gray-600 mt-1">On-Time Delivery</div>
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-lg">
                <div className="text-2xl font-bold text-[#00AEEF]">✓</div>
                <div className="text-sm text-gray-600 mt-1">Fair Pricing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00AEEF] to-[#0096d1] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Experience Our Service?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Join our growing list of satisfied customers. Get your free quote today!
          </p>
          <Button 
            onClick={() => navigate('/contact')}
            className="bg-white text-[#00AEEF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
          >
            Get Free Quote
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Testimonials;
